<script setup>
  import Layout from "@/layouts/main.vue";
  import PageHeader from "@/components/page-header";
  import '@jobinsjp/vue3-datatable/dist/style.css';
  import { DataTable, TableBody, TableHead, TableBodyCell } from '@jobinsjp/vue3-datatable';
  import moment from 'moment';
  import { truncate } from 'lodash';
  import Swal from 'sweetalert2';
  import axios from 'axios';
  import { authHeader } from './../../../helpers/authservice/auth-header';
  import { computed, ref } from 'vue';
  import { useStore } from 'vuex';
  import { required, helpers } from "@vuelidate/validators";
  import useVuelidate from "@vuelidate/core";

  const store = useStore();
  const loading = computed(() => store.state.globals.loading);
  const cameraTypes = computed(() => store.state.camera.cameraTypes);
  const pagination = computed(() => store.state.camera.cameraTypesPagination);
  const title = ref("Camera Types");
  const items = ref([
    {
      text: "Home",
      href: "/"
    },
    {
      text: "Camera",
    },
    {
      text: "Types",
      active: true,
    }
  ])

  const cameraType = ref({
    id: '',
    name: '',
    process: 'auto',
    folder: '',
    file_setting_id: 1,
    updated_at: null
  })

  const isLoading = ref(false);

  const notification = computed(() => store.state.notification);

  const query = ref({
    search: ''        
  })

  const rules = {
      name: {
          required: helpers.withMessage("Camera Type Name is required", required),
      },
      process: {
          required: helpers.withMessage("Camera Type Process is required", required),
      },
      folder: {
          required: helpers.withMessage("Camera Type Folder is required", required),
      },
      file_setting_id: {
          required: helpers.withMessage("Camera Type File Setting is required", required),
      },
  };

  const submitted = ref(false);

  const v$ = useVuelidate(rules, cameraType);


  const getAllCameraTypes = async (dt) => {
    isLoading.value = true

    await store.dispatch('camera/getCameraTypes', {
      page: dt.page,
      page_size: dt.per_page,
      sortColumn: 'updated_at',
      search: dt.search,
      orderBy: 'desc'
    });

    setTimeout(() => {
      isLoading.value = false
    }, 500)
  }

  const formattedDate = (date) => {
    if (!date) return 'N/A';
    return moment(date).format('YYYY-MM-DD h:mm A')
  }

  const getCameraTypeDetails = async (id) => {
    store.dispatch('globals/loading', null, { root: true });

    try {
        const { data } = await axios.get(`/camera_types/${id}`, {
            headers: authHeader()
        });
        cameraType.value = data.data;
        store.dispatch('notification/clear', null, { root: true });
        store.dispatch('globals/loaded', null, { root: true });
    } catch (err) {
        console.log(err);
        store.dispatch('globals/loaded', null, { root: true });
    }
}

  const deleteCameraType = (id, name) => {
      Swal.fire({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#34c38f",
        cancelButtonColor: "#f46a6a",
        confirmButtonText: "Delete",
      }).then(async (result) => {
        if (result.value) {
          await store.dispatch('camera/deleteCameraType', { id, name });
        }
      });
  }

  const updateCameraTypeDetails = async () => {
    try {
      submitted.value = true
      v$.value.$touch();
      if (v$.value.$invalid) return;
      await store.dispatch('camera/updateCameraType', cameraType.value)
    } catch (err) {
      console.log(err);
    }
  }
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <b-overlay :show="loading">
      <div class="d-flex mb-4 justify-content-between">
        <div>
          <h1 class="fs-4 text">Camera Types</h1>
          <small>Lists of all camera types</small>
        </div>
        <div>
          <router-link
              to="/camera/type/create"
              class="btn btn-primary right"
            >
              <i class="ri-add-fill label-icon align-middle fs-16 me-2"></i>
              New Camera Types
          </router-link>
        </div>
      </div>

      <div class="row">
          <div class="col lg-12">
              <data-table :rows="cameraTypes"
                  :pagination="pagination"
                  striped
                  :loading="loading"
                  :query="query"
                  filter
                  hoverable
                  sortable
                  @loadData="getAllCameraTypes"
              >
                <template #thead>
                    <table-head>Name</table-head>
                    <table-head>Process</table-head>
                    <table-head>Folder</table-head>
                    <table-head>File Settings</table-head>
                    <table-head>Date Modified</table-head>
                    <table-head>Action</table-head>
                </template>

                <template #tbody="{row}">
                    <table-body :title="row.name" v-text="truncate(row.name, { length : 20 })"/>
                    <table-body>
                      <b-badge :variant="row.process === 'auto' ? 'success' : 'warning'">
                        {{ row.process === 'auto' ? 'Auto' : 'Manual' }}
                      </b-badge>
                    </table-body>
                    <table-body v-text="row.folder" />
                    <table-body v-text="row.file_setting && row.file_setting.name ? row.file_setting.name : 'N/A'" />
                    <table-body>
                      {{ formattedDate(row.updated_at) }}
                    </table-body>
                    <table-body>
                      <button
                          type="button"
                          class="btn btn-secondary mr-2"
                          title="Edit"
                          data-bs-toggle="modal"
                          data-bs-target="#editCameraTypeModal"
                          @click="getCameraTypeDetails(row.id)"
                        >
                        <i class="mdi mdi-pencil label-icon align-middle"></i>
                      </button>
                      <button
                          @click="deleteCameraType(row.id, row.name)"
                          class="btn btn-danger"
                          title="Delete"
                      >
                        <i class="mdi mdi-delete"></i>
                      </button>
                    </table-body>
                </template>

                <template #empty>
                  <TableBodyCell colspan="10">
                      No camera types found.
                  </TableBodyCell>
              </template>
            </data-table>
          </div>
      </div>
    </b-overlay>
  </Layout>
   <!-- Varying modal content -->
  <div class="modal fade" role="dialog" id="editCameraTypeModal" tabindex="-1" aria-labelledby="varyingcontentModalLabel" aria-hidden="true">
      <div class="modal-dialog">
          <div class="modal-content">
              <b-overlay :show="loading">
                <div class="modal-header">
                    <h5 class="modal-title" id="varyingcontentModalLabel">Camera Type Details</h5>
                    <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                </div>
                <div class="modal-body">
                      <form>
                          <b-alert v-if="notification && notification.message"
                              show variant="success" class="fade show" role="alert">
                              {{ notification.message }}
                          </b-alert>

                          <div v-if="notification && notification.messages.length > 0">
                              <b-alert v-for="(message, index) in notification.messages" :key="index"
                                  show variant="danger" class="fade show" role="alert">
                                  {{ message }}
                              </b-alert>
                          </div>
                          <div class="mb-3">
                              <label for="name" class="form-label required">Name</label>
                              <input 
                                :class="{ 'is-invalid': submitted && v$.name.$error }"
                                type="text" class="form-control" id="name" v-model="cameraType.name">
                              <div
                                  v-for="item of v$.name.$errors"
                                  :key="item.$uid"
                                  class="invalid-feedback"
                              >
                                <span v-if="item.$message">{{ item.$message }}</span>
                              </div>
                          </div>
                          <div class="mb-3">
                              <label for="process" class="form-label required">Process</label>
                               <select id="process" v-model="cameraType.process" 
                                  :class="{ 'is-invalid': submitted && v$.process.$error }"
                                  class="form-select mb-3" aria-label="Camera Type Process Dropdown">
                                  <option value="auto">Auto</option>
                                  <option value="manual">Manual</option>
                              </select>
                               <div
                                  v-for="item of v$.process.$errors"
                                  :key="item.$uid"
                                  class="invalid-feedback"
                              >
                                <span v-if="item.$message">{{ item.$message }}</span>
                              </div>
                          </div>
                          <div class="mb-3">
                              <label for="folder" class="form-label required">Folder</label>
                              <input
                                  type="text"
                                  v-maska="'/XXXXXXXXXXXXXXXXXXXX'"
                                  :class="{ 'is-invalid': submitted && v$.folder.$error }"
                                  class="form-control" id="folder" v-model="cameraType.folder" />
                               <div class="text-muted">e.g /path-to-folder</div>
                               <div
                                  v-for="item of v$.folder.$errors"
                                  :key="item.$uid"
                                  class="invalid-feedback"
                              >
                                <span v-if="item.$message">{{ item.$message }}</span>
                              </div>
                          </div>
                          <div class="mb-3">
                              <label for="file-settings" class="form-label required">File Settings</label>
                               <select id="file-settings" v-model="cameraType.file_setting_id" class="form-select mb-3" aria-label="Camera Type File Settings Dropdown">
                                  <option value="1">s3</option>
                              </select>
                          </div>
                      </form>
                       <div class="text-muted">
                          Last Updated: {{ formattedDate(cameraType.updated_at) }}
                        </div>
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn btn-danger" data-bs-dismiss="modal">
                      <i class="mdi mdi-close-thick"></i>
                      Close
                    </button>
                    <button @click.prevent="updateCameraTypeDetails" type="button" class="btn btn-success">
                      <i class="mdi mdi-content-save"></i>
                      Update Camera Type
                    </button>
                </div>
              </b-overlay>
          </div>
      </div>
    </div>
</template>